import { useEffect, useRef, useState } from 'react';

/**
 * A 360° room view.
 *
 * WHY THIS IS HAND-WRITTEN WebGL rather than three.js: the obvious library is
 * ~600KB, the bundle is already flagged as too large for the phones this site
 * is for, and our Content-Security-Policy is `script-src 'self'` — so it could
 * not come from a CDN and would have to be bundled whole. What is actually
 * needed is one full-screen quad and a fragment shader that turns a screen
 * pixel into a direction and looks it up in an equirectangular image. That is
 * the eighty lines below, and it carries no dependency at all.
 *
 * WHY NOT just scroll the image sideways, which is the common cheap trick: an
 * equirectangular photo is a sphere unwrapped onto a rectangle, so panning it
 * flat leaves the ceiling and floor stretched into unreadable smears and
 * straight walls visibly bowed. A renter looking at a bent kitchen assumes the
 * kitchen is bent. The projection below is the difference between a 360 photo
 * and a photo of a 360.
 *
 * Degrades honestly: no WebGL means the flat image with a note saying what it
 * is, never a silently broken canvas.
 */

export const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() { vUv = aPos; gl_Position = vec4(aPos, 0.0, 1.0); }
`;

export const FRAG = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform float uYaw, uPitch, uFov, uAspect;
const float PI = 3.14159265358979;
void main() {
  // Screen pixel -> a ray in camera space, then rotated by pitch and yaw.
  float f = tan(uFov * 0.5);
  vec3 dir = normalize(vec3(vUv.x * f * uAspect, vUv.y * f, -1.0));
  float cp = cos(uPitch), sp = sin(uPitch);
  dir = vec3(dir.x, dir.y * cp - dir.z * sp, dir.y * sp + dir.z * cp);
  float cy = cos(uYaw), sy = sin(uYaw);
  dir = vec3(dir.x * cy + dir.z * sy, dir.y, -dir.x * sy + dir.z * cy);
  // Direction -> longitude/latitude -> the equirectangular texel.
  float lon = atan(dir.x, -dir.z);
  float lat = asin(clamp(dir.y, -1.0, 1.0));
  gl_FragColor = texture2D(uTex, vec2(lon / (2.0 * PI) + 0.5, 0.5 - lat / PI));
}
`;

export const MIN_FOV = 0.5;                    // ~29°, zoomed in
export const MAX_FOV = 1.75;                   // ~100°, wide
export const MAX_PITCH = Math.PI / 2 - 0.01;   // stop just short of the poles

export function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(sh) ?? 'shader failed to compile');
  }
  return sh;
}

export function Panorama360({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  // Shown until the first drag, so nobody stares at a still picture not
  // realising it moves.
  const [hinted, setHinted] = useState(false);

  // Camera lives in a ref, not state: it changes every animation frame while
  // dragging, and re-rendering React sixty times a second to move a camera
  // would be absurd.
  const cam = useRef({ yaw: 0, pitch: 0, fov: 1.2 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', { antialias: true, alpha: false });
    if (!gl) { setFailed(true); return; }

    let program: WebGLProgram;
    try {
      program = gl.createProgram()!;
      gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('link failed');
    } catch { setFailed(true); return; }

    gl.useProgram(program);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uYaw = gl.getUniformLocation(program, 'uYaw');
    const uPitch = gl.getUniformLocation(program, 'uPitch');
    const uFov = gl.getUniformLocation(program, 'uFov');
    const uAspect = gl.getUniformLocation(program, 'uAspect');

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // A single blue-grey texel stands in until the photo decodes, so the first
    // frame is the page's own background rather than uninitialised memory.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([40, 44, 52, 255]));

    let frame = 0;
    let disposed = false;

    const draw = () => {
      if (disposed) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform1f(uYaw, cam.current.yaw);
      gl.uniform1f(uPitch, cam.current.pitch);
      gl.uniform1f(uFov, cam.current.fov);
      gl.uniform1f(uAspect, canvas.width / canvas.height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    /*
     * Coalesces the many redraws of a DRAG into one per displayed frame.
     *
     * Only interaction goes through here. A one-off redraw — the image
     * arriving, the panel gaining a width, the tab becoming visible — calls
     * draw() directly instead, because requestAnimationFrame does not fire in
     * a hidden tab and there is nothing to coalesce: waiting for a frame that
     * may never come is how the viewer ended up blank in a background tab.
     */
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; draw(); });
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (disposed) return;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      // A panorama wraps horizontally, so REPEAT on S removes the seam behind
      // the viewer — but REPEAT is only legal on power-of-two textures, and an
      // uploaded file may be anything. Ask, rather than assume.
      const pot = (n: number) => (n & (n - 1)) === 0;
      const wrapS = pot(img.width) && pot(img.height) ? gl.REPEAT : gl.CLAMP_TO_EDGE;
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapS);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      setReady(true);
      draw();
    };
    img.onerror = () => { if (!disposed) setFailed(true); };
    img.src = src;

    /* ----- looking around ------------------------------------------------ */
    let dragging = false, lastX = 0, lastY = 0, pointer = -1;

    const down = (e: PointerEvent) => {
      dragging = true; pointer = e.pointerId; lastX = e.clientX; lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
      setHinted(true);
    };
    const move = (e: PointerEvent) => {
      if (!dragging || e.pointerId !== pointer) return;
      // Scaled by the field of view so a drag moves the same amount of PICTURE
      // whether zoomed in or out, which is what the hand expects.
      const k = cam.current.fov / canvas.clientHeight;
      cam.current.yaw -= (e.clientX - lastX) * k;
      cam.current.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH,
        cam.current.pitch + (e.clientY - lastY) * k));
      lastX = e.clientX; lastY = e.clientY;
      schedule();
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId !== pointer) return;
      dragging = false; pointer = -1;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    };
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      cam.current.fov = Math.max(MIN_FOV, Math.min(MAX_FOV, cam.current.fov + e.deltaY * 0.0015));
      schedule();
    };
    const key = (e: KeyboardEvent) => {
      const step = cam.current.fov * 0.12;
      if (e.key === 'ArrowLeft') cam.current.yaw -= step;
      else if (e.key === 'ArrowRight') cam.current.yaw += step;
      else if (e.key === 'ArrowUp') cam.current.pitch = Math.min(MAX_PITCH, cam.current.pitch + step);
      else if (e.key === 'ArrowDown') cam.current.pitch = Math.max(-MAX_PITCH, cam.current.pitch - step);
      else return;
      e.preventDefault(); setHinted(true); schedule();
    };

    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('wheel', wheel, { passive: false });
    canvas.addEventListener('keydown', key);

    /*
     * Redraw whenever the canvas GAINS OR CHANGES SIZE, and whenever the page
     * becomes visible again. Both matter, and a window 'resize' listener
     * covers neither.
     *
     * requestAnimationFrame does not fire in a hidden tab. So a listing opened
     * in a background tab — an ordinary thing to do from a list of search
     * results — mounted this viewer, loaded the image, asked for a frame, and
     * got none. Switching to the tab fired no resize and no scroll, so nothing
     * ever asked again: the reader arrived at a blank box captioned "drag to
     * look around". Caught because the browser pane used for verification is
     * itself hidden, which turned out to be the same condition.
     *
     * A ResizeObserver also covers the cases a window resize misses entirely:
     * the enquiry sidebar reflowing, or this panel appearing in a tab that was
     * laid out at zero width.
     */
    const observer = new ResizeObserver(() => draw());
    observer.observe(canvas);
    const onVisible = () => { if (!document.hidden) draw(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      if (frame) cancelAnimationFrame(frame);
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      canvas.removeEventListener('wheel', wheel);
      canvas.removeEventListener('keydown', key);
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisible);
      gl.deleteTexture(tex);
      gl.deleteBuffer(buf);
      gl.deleteProgram(program);
    };
  }, [src]);

  if (failed) {
    return (
      <div className={`pano pano-fallback ${className ?? ''}`}>
        <img src={src} alt={alt} />
        <p className="muted pano-note">
          This is a 360° photo. Your browser cannot display it interactively, so it is shown flat.
        </p>
      </div>
    );
  }

  return (
    <div className={`pano ${className ?? ''}`}>
      <canvas
        ref={canvasRef}
        className="pano-canvas"
        tabIndex={0}
        role="img"
        aria-label={`${alt}. 360 degree view — drag, or use the arrow keys, to look around.`}
      />
      {!ready && <div className="pano-loading">Loading the 360° view…</div>}
      {ready && !hinted && <div className="pano-hint" aria-hidden>Drag to look around</div>}
      <span className="pano-badge" aria-hidden>360°</span>
    </div>
  );
}
